/**
 * mmc_cookie_guardian.js
 * Professional Cookie Consent and Analytics integration.
 */

import React, { useState, useEffect } from 'react';
import { Cookie, ShieldCheck } from '@phosphor-icons/react';
import { MmcButton } from './ui';
import '../styles/mmc-cookies.css';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_ID;

const MmcCookieGuardian = () => {
    const [status, setStatus] = useState('loading'); // loading, prompt, accepted, declined

    useEffect(() => {
        // Only proceed if we have a valid GA ID
        if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
            console.warn('[Guardian] No valid Google Analytics ID found. Consent will still be tracked.');
        }
        const consent = localStorage.getItem('mmc_cookie_consent');
        if (consent === 'accepted') {
            enableAnalytics();
            setStatus('accepted');
        } else if (consent === 'declined') {
            setStatus('declined');
        } else {
            setStatus('prompt');
        }
    }, []);

    const enableAnalytics = () => {
        if (typeof window === 'undefined') return;

        // Standard Google Analytics injection
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', GA_MEASUREMENT_ID, {
            anonymize_ip: true,
            cookie_flags: 'SameSite=None;Secure'
        });

        console.log('[Guardian] Analytics Enabled');
    };

    const handleAccept = () => {
        localStorage.setItem('mmc_cookie_consent', 'accepted');
        enableAnalytics();
        setStatus('accepted');
    };

    const handleDecline = () => {
        localStorage.setItem('mmc_cookie_consent', 'declined');
        setStatus('declined');
    };

    if (status !== 'prompt') return null;

    return (
        <div className="mmc-cookie-guardian" role="complementary" aria-labelledby="cookie-title">
            <div className="mmc-cookie-card">
                <div id="cookie-title" className="mmc-cookie-title">
                    <Cookie size={32} weight="duotone" />
                    <span>Privacy Shield</span>
                </div>
                <p className="mmc-cookie-text">
                    We use anonymous analytics to understand how players use our games.
                    No personal data is collected, only usage patterns to help us improve.
                </p>
                <div className="mmc-cookie-actions">
                    <MmcButton
                        variant="ghost"
                        onClick={handleDecline}
                        aria-label="Decline non-essential cookies"
                    >
                        Decline
                    </MmcButton>
                    <MmcButton
                        variant="primary"
                        onClick={handleAccept}
                        icon={ShieldCheck}
                        aria-label="Accept analytics cookies"
                    >
                        Accept All
                    </MmcButton>
                </div>
            </div>
        </div>
    );
};

export default MmcCookieGuardian;
